<script setup lang="ts">
import { computed, h, onMounted, ref, watch, type VNode } from 'vue';
import { RouterLink, useRouter, type RouteLocationRaw } from 'vue-router';
import { NIcon, type DataTableColumns, type PaginationInfo } from 'naive-ui';
import { IconFolderOpen, IconSearch } from '@/components/design/icons';
import type { ReviewProjectInDb } from '@/api';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import ReviewProjectBreadcrumb from '@/components/reviewProject/ReviewProjectBreadcrumb.vue';
import { useListQuerySync, usePaginationConfig } from '@/composables/useListQuerySync';
import { ReviewProjectService } from '@/services/reviewProjectService';
import { formatDateTimeForTable } from '@/utils/datetimeUtils';
import { extractErrorDetail } from '@/utils/errorHandler';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { reviewProjectRoute } from '@/router/links';
import GaiaVaultFolderPicker from '@/components/design/domain/fileVault/GaiaVaultFolderPicker.vue';
import type { VaultFolderPickerLocation } from '@/components/design/domain/fileVault/vaultFolderPicker';

const router = useRouter();
const message = useGaiaToast();

const projects = ref<ReviewProjectInDb[]>([]);
const totalCount = ref<number>(0);
const loading = ref<boolean>(false);
const error = ref<string | null>(null);
const { searchQuery, currentPage, pageSize } = useListQuerySync();

async function loadProjects(): Promise<void> {
  loading.value = true;
  error.value = null;
  try {
    const result = await ReviewProjectService.search({
      text: searchQuery.value.trim(),
      page: currentPage.value,
      pageSize: pageSize.value,
    });
    projects.value = result.results;
    totalCount.value = result.result_count;
  } catch (error_) {
    error.value = extractErrorDetail(error_, 'Failed to load review projects.');
  } finally {
    loading.value = false;
  }
}

// Page 1 is reached either by fetching directly or by letting the page watcher
// fire — never both, so a search change costs one request.
function reloadFromFirstPage(): void {
  if (currentPage.value === 1) {
    void loadProjects();
  } else {
    currentPage.value = 1;
  }
}

// The empty state's own "Clear search" button — the field's clear is handled by
// its `search` hook.
function clearSearch(): void {
  searchQuery.value = '';
  reloadFromFirstPage();
}

watch([currentPage, pageSize], () => {
  void loadProjects();
});

const paginationConfig = usePaginationConfig(currentPage, pageSize, totalCount);

const pagination = computed(() => ({
  ...paginationConfig.value,
  prefix: ({ startIndex, endIndex, itemCount }: PaginationInfo): string =>
    itemCount === 0 ? '0 of 0' : `${startIndex + 1}–${endIndex + 1} of ${itemCount}`,
}));

const countLabel = computed(() =>
  totalCount.value === 1 ? '1 project' : `${totalCount.value} projects`
);

// Which empty block the table shows when it has no rows.
const emptyVariant = computed<'error' | 'no-match' | 'first-run'>(() => {
  if (error.value) return 'error';
  return searchQuery.value.trim() ? 'no-match' : 'first-run';
});

function projectRoute(project: ReviewProjectInDb): RouteLocationRaw {
  return reviewProjectRoute(project.uid);
}

function openProject(project: ReviewProjectInDb): void {
  void router.push(projectRoute(project));
}

/* ---------------------------------------------------------------- create */

const showCreateModal = ref<boolean>(false);
const creating = ref<boolean>(false);
const newName = ref<string>('');
/** The vault folder the picker is in — it becomes the project's data room. */
const dataRoomFolder = ref<VaultFolderPickerLocation | null>(null);

// The API refuses the vault root (and checks the rest: overlap with another
// data room, READ|WRITE on everything inside); the root is the only case the
// picker can tell up front.
const dataRoomFolderValid = computed<boolean>(
  () => dataRoomFolder.value !== null && !dataRoomFolder.value.isRoot
);
const canCreate = computed<boolean>(
  () => Boolean(newName.value.trim()) && dataRoomFolderValid.value
);

function openCreateModal(): void {
  newName.value = '';
  dataRoomFolder.value = null;
  showCreateModal.value = true;
}

async function handleCreate(): Promise<void> {
  const name = newName.value.trim();
  const folder = dataRoomFolder.value;
  if (!canCreate.value || !folder || creating.value) return;

  creating.value = true;
  try {
    // allow_duplicate_runs stays at the backend default; it is toggled in the
    // project settings, not at create time.
    const project = await ReviewProjectService.create({
      name,
      data_room_directory_uid: folder.uid,
    });
    showCreateModal.value = false;
    openProject(project);
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to create review project.'));
  } finally {
    creating.value = false;
  }
}

/* ---------------------------------------------------------------- delete */

// Deleting takes the playbooks, runs, chats, report and Q&As with it (the data
// room folder stays in the vault), so this one keeps the typed confirmation rather than dropping to the
// inline confirm strip the other lists use.
const showDeleteModal = ref<boolean>(false);
const deleting = ref<boolean>(false);
const projectPendingDeletion = ref<ReviewProjectInDb | null>(null);
const deleteConfirmation = ref<string>('');
const deleteNameMatches = computed<boolean>(
  () => deleteConfirmation.value === projectPendingDeletion.value?.name
);

function openDeleteModal(project: ReviewProjectInDb): void {
  projectPendingDeletion.value = project;
  deleteConfirmation.value = '';
  showDeleteModal.value = true;
}

async function handleDelete(): Promise<void> {
  const project = projectPendingDeletion.value;
  if (!project || !deleteNameMatches.value || deleting.value) return;

  deleting.value = true;
  try {
    await ReviewProjectService.delete(project.uid);
    message.success(`Review project “${project.name}” deleted.`);
    showDeleteModal.value = false;
    projectPendingDeletion.value = null;
    if (projects.value.length === 1 && currentPage.value > 1) {
      currentPage.value -= 1; // the page watcher reloads
    } else {
      await loadProjects();
    }
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to delete review project.'));
  } finally {
    deleting.value = false;
  }
}

/* ----------------------------------------------------------------- table */

const columns = computed<DataTableColumns<ReviewProjectInDb>>(() => [
  {
    title: 'Name',
    key: 'name',
    // A real link so cmd- and middle-click open the project in a new tab.
    render: (row): VNode =>
      h(
        RouterLink,
        {
          to: projectRoute(row),
          style: 'color: inherit; text-decoration: none; display: block; min-width: 0',
          onClick: (e: MouseEvent) => {
            e.stopPropagation();
          },
        },
        {
          default: () =>
            h(GaiaText, { weight: 'semibold', tone: 'ink', truncate: true }, () => row.name),
        }
      ),
  },
  {
    title: 'Owner',
    key: 'owner_user',
    width: 220,
    ellipsis: { tooltip: true },
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
    render: (row): string => row.owner_user?.full_name || row.owner_user?.email || '—',
  },
  {
    title: 'Created',
    key: 'created_at',
    width: 190,
    render: (row): string => formatDateTimeForTable(row.created_at),
  },
  {
    title: '',
    key: 'actions',
    width: 60,
    align: 'right',
    render: (row): VNode =>
      h(
        'div',
        {
          onClick: (event: MouseEvent): void => {
            // The whole row navigates to the project on click.
            event.stopPropagation();
          },
        },
        h(GaiaDeleteButton, {
          size: 'md',
          subject: 'project',
          onClick: (): void => {
            openDeleteModal(row);
          },
        })
      ),
  },
]);

function rowProps(row: ReviewProjectInDb): Record<string, unknown> {
  return {
    style: 'cursor: pointer;',
    onClick: (): void => {
      openProject(row);
    },
  };
}

onMounted(() => {
  void loadProjects();
});
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <review-project-breadcrumb />
    </template>

    <n-flex vertical :size="20">
      <n-flex align="flex-start" justify="space-between" :size="24">
        <n-flex vertical :size="6" :style="{ flex: '1 1 320px', minWidth: 0 }">
          <n-flex align="baseline" :size="10">
            <gaia-header :level="1" variant="display" size="screen" style="margin: 0">
              Review projects
            </gaia-header>
            <gaia-count-label>{{ countLabel }}</gaia-count-label>
          </n-flex>
          <gaia-text size="body" tone="tertiary" :style="{ maxWidth: '60ch' }">
            Organize contract reviews into shared projects.
          </gaia-text>
        </n-flex>
        <gaia-create-button
          subject="project"
          data-tour="rp.create-button"
          @click="openCreateModal"
        />
      </n-flex>

      <gaia-search-input
        v-model:value="searchQuery"
        placeholder="Search review projects"
        @search="reloadFromFirstPage"
      />

      <gaia-table
        data-tour="rp.projects-table"
        :columns="columns"
        :data="projects"
        :loading="loading"
        :remote="true"
        :pagination="projects.length > 0 ? pagination : false"
        :row-key="(row: ReviewProjectInDb) => row.uid"
        :row-props="rowProps"
      >
        <template #empty>
          <gaia-empty-state
            v-if="emptyVariant === 'error'"
            status="alert"
            title="Couldn’t load review projects"
          >
            {{ error }}
            <template #meta>review-project/search</template>
            <template #actions>
              <gaia-button size="small" type="primary" @click="loadProjects">Retry</gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state
            v-else-if="emptyVariant === 'no-match'"
            :title="`No projects match “${searchQuery}”`"
          >
            <template #icon><n-icon :component="IconSearch" :size="18" /></template>
            Search covers project names and owners. Try a shorter term, or clear the search to see
            all projects.
            <template #actions>
              <gaia-button size="small" @click="clearSearch">Clear search</gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else title="No review projects yet">
            <template #icon><n-icon :component="IconFolderOpen" :size="18" /></template>
            A project is a shared workspace: one data room of contracts, the analyses run over them,
            and the report the team writes from the findings.
            <template #actions>
              <gaia-create-button subject="project" :icon="false" @click="openCreateModal" />
            </template>
          </gaia-empty-state>
        </template>
      </gaia-table>
    </n-flex>
  </Layout>

  <gaia-modal
    v-model:show="showCreateModal"
    title="Create review project"
    width="560px"
    :mask-closable="false"
  >
    <n-flex vertical :size="14">
      <gaia-text size="prose" tone="subtle">
        Name the project and pick the vault folder that holds its documents. Analyses are added
        afterwards.
      </gaia-text>
      <gaia-form-field label="Project name">
        <gaia-input
          v-model:value="newName"
          placeholder="e.g. Kova AG vendor wave"
          @keydown.enter="handleCreate"
        />
      </gaia-form-field>
      <gaia-form-field label="Data room folder">
        <n-flex vertical :size="8">
          <gaia-vault-folder-picker
            :active="showCreateModal"
            :disabled="creating"
            allow-create
            empty-hint="Pick this folder as the data room, or open one of its subfolders."
            @update:location="dataRoomFolder = $event"
          />
          <gaia-text v-if="dataRoomFolder?.isRoot" size="control" tone="tertiary">
            Open the folder that holds the project's documents, or create a new one.
          </gaia-text>
          <gaia-text v-else-if="dataRoomFolder" size="control" tone="tertiary">
            Data room: <strong>{{ dataRoomFolder?.label }}</strong
            >. Project members get read and write access to everything inside it. You need that
            access yourself; an empty folder always works.
          </gaia-text>
        </n-flex>
      </gaia-form-field>
    </n-flex>
    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button @click="showCreateModal = false">Cancel</gaia-button>
        <gaia-create-button
          subject="project"
          :icon="false"
          :loading="creating"
          :disabled="!canCreate"
          @click="handleCreate"
        />
      </n-flex>
    </template>
  </gaia-modal>

  <gaia-modal
    v-model:show="showDeleteModal"
    title="Delete review project"
    width="560px"
    :mask-closable="false"
  >
    <n-flex vertical :size="14">
      <gaia-banner type="error" title="This action cannot be undone">
        Deleting <strong>{{ projectPendingDeletion?.name }}</strong> also removes the playbooks
        created from it, their runs, the chats, the report and the Q&amp;As. The data room folder
        stays in the vault untouched.
      </gaia-banner>
      <gaia-form-field :label="`Type “${projectPendingDeletion?.name ?? ''}” to confirm`">
        <gaia-input
          v-model:value="deleteConfirmation"
          :placeholder="projectPendingDeletion?.name"
          @keydown.enter="handleDelete"
        />
      </gaia-form-field>
    </n-flex>
    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button @click="showDeleteModal = false">Cancel</gaia-button>
        <gaia-button
          type="error"
          :disabled="!deleteNameMatches"
          :loading="deleting"
          @click="handleDelete"
        >
          Delete project
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
